import { adminHttp } from "@shared/lib/http";
import { translateUi } from "@i18n/config";

export const ADMIN_PUSH_SERVICE_WORKER_SCOPE = import.meta.env.DEV
  ? "/admin-push/"
  : "/";

export type AdminNotification = {
  id: string;
  type: string;
  priority: "normal" | "high" | string;
  title: string;
  body: string;
  actionUrl?: string | null;
  createdAtUtc: string;
  readAtUtc?: string | null;
};

export type AdminNotificationPage = {
  total: number;
  page: number;
  pageSize: number;
  items: AdminNotification[];
};

export type AdminPushConfiguration = {
  enabled: boolean;
  publicKey: string | null;
};

export async function listAdminNotifications(
  page = 1,
  pageSize = 20,
): Promise<AdminNotificationPage> {
  const result: any = await adminHttp.get(
    `/api/admin/notifications?page=${encodeURIComponent(page)}&pageSize=${encodeURIComponent(pageSize)}`,
  );
  return {
    total: Math.max(0, Number(result?.total || 0)),
    page: Math.max(1, Number(result?.page || page)),
    pageSize: Math.max(1, Number(result?.pageSize || pageSize)),
    items: Array.isArray(result?.items) ? result.items : [],
  };
}

export async function getAdminUnreadNotificationCount(): Promise<number> {
  const result: any = await adminHttp.get(
    "/api/admin/notifications/unread-count",
  );
  return Math.max(0, Number(result?.count || 0));
}

export async function markAdminNotificationRead(id: string): Promise<void> {
  await adminHttp.post(
    `/api/admin/notifications/${encodeURIComponent(id)}/read`,
    {},
  );
  notifyNotificationStateChanged();
}

export async function markAllAdminNotificationsRead(): Promise<void> {
  await adminHttp.post("/api/admin/notifications/read-all", {});
  notifyNotificationStateChanged();
}

export async function getAdminPushConfiguration(): Promise<AdminPushConfiguration> {
  const result: any = await adminHttp.get(
    "/api/admin/notifications/push/config",
  );
  return {
    enabled: Boolean(result?.enabled && result?.publicKey),
    publicKey: typeof result?.publicKey === "string" ? result.publicKey : null,
  };
}

export async function registerAdminPushSubscription(
  subscription: PushSubscription,
): Promise<void> {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error(
      translateUi(
        "theBrowserReturnedAnIncompletePushSubscription",
        "The browser returned an incomplete push subscription.",
      ),
    );
  }
  await adminHttp.post("/api/admin/notifications/push/subscribe", {
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
    userAgent: navigator.userAgent,
  });
}

export async function revokeAdminPushSubscription(
  endpoint: string,
): Promise<void> {
  await adminHttp.post("/api/admin/notifications/push/unsubscribe", {
    endpoint,
  });
}

export async function unsubscribeAdminPushOnLogout(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator))
    return;
  try {
    const registration = await navigator.serviceWorker.getRegistration(
      ADMIN_PUSH_SERVICE_WORKER_SCOPE,
    );
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    try {
      await revokeAdminPushSubscription(subscription.endpoint);
    } finally {
      await subscription.unsubscribe();
    }
  } catch {
    // Logout must continue even when browser push cleanup is unavailable.
  }
}

function notifyNotificationStateChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("storizone:admin-notifications-changed"),
    );
  }
}
